import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorItemProps } from "@/v2-backoffice-client/components/selector"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { describeMergedInto } from "@/v2-backoffice-client/customer/merge/customer.merge.utils"
import {
  countMembersOwnedElsewhere,
  formatCountOfTotal,
  formatOwnerNames,
} from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { useTenantCustomerAssignMutation } from "@/v2-backoffice-client/tenant/customer/add/tenant.customer.assign.mutation"
import { useCustomerTenantRemoveMutation } from "@/v2-backoffice-client/customer/tenant/remove/customer.tenant.remove.mutation"

export type TenantCustomerAddSelectorItemProps = Omit<
  SelectorItemProps,
  "value" | "children" | "onSelect" | "onDeselect" | "warning"
> & {
  customer: CustomerFragment
  tenantId: number
  /**
   * The team's live members, for the guard: a customer that owns none of
   * them while another customer owns them is almost always the wrong row
   * (the Fagerstad team sat under Trollfjord / Hadsel for 22 months this
   * way), so it takes a confirmation.
   */
  members?: Pick<UserFragment, "Customer">[]
  onSelect?(): void
  onDeselect?(): void
}

function TenantCustomerAddSelectorItem(
  props: TenantCustomerAddSelectorItemProps,
) {
  const {
    customer,
    tenantId,
    members = [],
    onSelect,
    onDeselect,
    ...selectorItemProps
  } = props

  const [assign, { loading: assigning, error: assignError }] =
    useTenantCustomerAssignMutation()

  const [removeTenant, { loading: removingTenant, error: removeError }] =
    useCustomerTenantRemoveMutation()

  const loading = assigning || removingTenant
  const error = assignError || removeError

  errorToast(error, {
    title: "Failed to update the team's customer",
  })

  const isLinked = Boolean(customer.ExternalCustomerId)
  const mergedAway = describeMergedInto(customer)
  const elsewhere = countMembersOwnedElsewhere(members, customer)
  const ownedCount = members.filter(
    (member) =>
      member.Customer?.ExternalCustomerId === customer.ExternalCustomerId,
  ).length

  function describe() {
    if (mergedAway) return mergedAway
    if (!isLinked) {
      return "Not linked to HubSpot — link a company before it can own a team"
    }
    if (members.length > 0 && ownedCount > 0) {
      return `${customer.ExternalCustomerId} - owns ${formatCountOfTotal(ownedCount, members.length, "member")}`
    }
    return `${customer.ExternalCustomerId} - ${customer.UserCount} users`
  }

  return (
    <Selector.Item
      isLoading={loading}
      value={String(customer.Id)}
      disabled={!isLinked || Boolean(mergedAway)}
      warning={
        elsewhere.count > 0 && ownedCount === 0
          ? `${formatCountOfTotal(elsewhere.count, elsewhere.total, "member")} ${elsewhere.count === 1 ? "is" : "are"} owned by ${formatOwnerNames(elsewhere.ownerNames)}, not by this customer.`
          : undefined
      }
      confirmContent="Update anyway?"
      onSelect={() => {
        // Tenant assignment still keys on the legacy HubSpot id; an
        // app-created customer must be linked to a company first.
        if (!customer.ExternalCustomerId) return
        assign({
          variables: {
            tenantId,
            externalCustomerId: customer.ExternalCustomerId,
          },
        })
        onSelect?.()
      }}
      onDeselect={() => {
        removeTenant({ variables: { customerId: 0, tenantId } })
        onDeselect?.()
      }}
      {...selectorItemProps}
    >
      <Selector.ItemText
        primary={customer.Name ?? "Unnamed"}
        secondary={describe()}
      />
    </Selector.Item>
  )
}

export default TenantCustomerAddSelectorItem
